import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuthContext } from '@/components/auth/ClerkAuthProvider';
import { useCreateLeadCompat as useCreateLead } from '@/hooks/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FormError } from '@/components/ui/form-error';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { newLeadSchema, type NewLeadFormData } from '@/lib/validations';

interface NewLeadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ROOM_TYPES = [
  { value: 'kitchen', label: 'Kitchen' },
  { value: 'bathroom', label: 'Bathroom' },
  { value: 'basement', label: 'Basement' },
  { value: 'bedroom', label: 'Bedroom' },
  { value: 'living_room', label: 'Living Room' },
  { value: 'other', label: 'Other' },
];

const ROOM_STATES = [
  { value: 'needs_full_renovation', label: 'Needs Full Renovation' },
  { value: 'needs_partial_update', label: 'Needs Partial Update' },
  { value: 'cosmetic_refresh', label: 'Cosmetic Refresh Only' },
  { value: 'new_construction', label: 'New Construction' },
];

export function NewLeadDialog({ open, onOpenChange }: NewLeadDialogProps) {
  const { tenantId } = useAuthContext();
  const createLead = useCreateLead();

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NewLeadFormData>({
    resolver: zodResolver(newLeadSchema),
    defaultValues: {
      clientName: '',
      clientPhone: '',
      roomType: undefined,
      roomState: undefined,
      materials: '',
      notes: '',
    },
  });

  const onSubmit = async (data: NewLeadFormData) => {
    if (!tenantId) {
      toast.error('No tenant ID found');
      return;
    }

    try {
      await createLead.mutateAsync({
        tenantId,
        data: {
          client_name: data.clientName.trim(),
          client_phone: data.clientPhone.trim(),
          verification_status: 'pending',
          gemini_analysis: {
            room_type: data.roomType,
            room_state: data.roomState,
            materials: data.materials
              ? data.materials.split(',').map((m) => m.trim()).filter(Boolean)
              : [],
            notes: data.notes?.trim() || '',
          },
        },
      });

      toast.success('Lead created successfully!', {
        description: `${data.clientName} has been added to your leads.`,
      });

      reset();
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating lead:', error);
      toast.error('Failed to create lead', {
        description: 'Please try again or contact support.',
      });
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      reset();
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Add New Lead
          </DialogTitle>
          <DialogDescription>
            Manually add a new renovation lead to your dashboard.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
          {/* Client Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="clientName">
                Client Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="clientName"
                autoFocus
                placeholder="John Smith"
                {...register('clientName')}
                disabled={createLead.isPending}
                className={errors.clientName ? 'border-destructive' : ''}
              />
              <FormError message={errors.clientName?.message} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="clientPhone">
                Phone Number <span className="text-destructive">*</span>
              </Label>
              <Input
                id="clientPhone"
                type="tel"
                placeholder="(555) 123-4567"
                {...register('clientPhone')}
                disabled={createLead.isPending}
                className={errors.clientPhone ? 'border-destructive' : ''}
              />
              <FormError message={errors.clientPhone?.message} />
            </div>
          </div>

          {/* Room Details */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="roomType">
                Room Type <span className="text-destructive">*</span>
              </Label>
              <Controller
                name="roomType"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={createLead.isPending}
                  >
                    <SelectTrigger
                      id="roomType"
                      className={errors.roomType ? 'border-destructive' : ''}
                    >
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {ROOM_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              <FormError message={errors.roomType?.message} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="roomState">
                Current State <span className="text-destructive">*</span>
              </Label>
              <Controller
                name="roomState"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={createLead.isPending}
                  >
                    <SelectTrigger
                      id="roomState"
                      className={errors.roomState ? 'border-destructive' : ''}
                    >
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {ROOM_STATES.map((state) => (
                        <SelectItem key={state.value} value={state.value}>
                          {state.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              <FormError message={errors.roomState?.message} />
            </div>
          </div>

          {/* Materials */}
          <div className="space-y-2">
            <Label htmlFor="materials">Preferred Materials</Label>
            <Input
              id="materials"
              placeholder="Quartz countertops, hardwood floors, shaker cabinets"
              {...register('materials')}
              disabled={createLead.isPending}
              className={errors.materials ? 'border-destructive' : ''}
            />
            <FormError message={errors.materials?.message} />
            <p className="text-xs text-muted-foreground">
              Comma-separated list of materials the client mentioned (max 500 characters)
            </p>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Additional Notes</Label>
            <Textarea
              id="notes"
              placeholder="Any additional details about the project..."
              {...register('notes')}
              rows={3}
              disabled={createLead.isPending}
              className={errors.notes ? 'border-destructive' : ''}
            />
            <FormError message={errors.notes?.message} />
            <p className="text-xs text-muted-foreground">
              Max 500 characters
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={createLead.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createLead.isPending}>
              {createLead.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Create Lead
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
